/**
 * Budget Data Hooks using TanStack Query (React Query) v5
 *
 * Provides optimized data fetching and caching for budget-related operations with:
 * - 5-minute stale time for budget endpoints (AC1)
 * - Automatic cache invalidation after mutations (AC2)
 * - Tenant-aware query keys
 * - Comprehensive error handling
 *
 * @module hooks/useBudgets
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query'
import { budgetQueryKeys, invalidateBudgetQueries } from '@/constants/queryKeys'
import {
  getClientBudget,
  updateClientBudget,
  listClientBudgets,
  getTrainingBudget,
  updateTrainingBudget,
  getProjectionsSummary,
  type BudgetApiError,
} from '@/lib/services/budgets.service'
import type {
  ClientBudgetSettings,
  ClientBudgetsList,
  TrainingBudget,
  ProjectionsSummary,
  UpdateClientBudgetRequest,
  UpdateTrainingBudgetRequest,
} from '@/types/budgets'

/**
 * Fetch client budget for a specific year
 *
 * Automatically caches data for 5 minutes (AC1).
 * Refetches when window regains focus or network reconnects.
 *
 * @param tenantDomain - Tenant domain (e.g., "monumenta.localhost")
 * @param clientCode - Client code (e.g., "000054")
 * @param year - Budget year (e.g., 2025)
 * @param enabled - Whether query should run (default: true)
 *
 * @example
 * ```typescript
 * const { data, isLoading, error } = useClientBudget(
 *   'monumenta.localhost',
 *   '000054',
 *   2025
 * )
 *
 * if (isLoading) return <Skeleton />
 * if (error) return <ErrorMessage error={error} />
 * return <BudgetDisplay budget={data} />
 * ```
 */
export function useClientBudget(
  tenantDomain: string,
  clientCode: string,
  year: number,
  enabled = true
): UseQueryResult<ClientBudgetSettings, BudgetApiError> {
  return useQuery({
    queryKey: budgetQueryKeys.clientBudgets.detail(tenantDomain, clientCode, year),
    queryFn: () => getClientBudget(clientCode, year),
    enabled: enabled && !!tenantDomain && !!clientCode && !!year,
    // Inherits staleTime: 5 minutes from global config (AC1)
  })
}

/**
 * Update client budget for a specific year
 *
 * Automatically invalidates related queries after successful update (AC2).
 *
 * @param tenantDomain - Tenant domain
 * @param clientCode - Client code
 * @param year - Budget year
 *
 * @example
 * ```typescript
 * const updateMutation = useUpdateClientBudget('monumenta.localhost', '000054', 2025)
 *
 * const handleSave = async (settings) => {
 *   try {
 *     await updateMutation.mutateAsync(settings)
 *     showSuccessToast('Budget updated successfully')
 *   } catch (error) {
 *     showErrorToast(error.userMessage)
 *   }
 * }
 * ```
 */
export function useUpdateClientBudget(
  tenantDomain: string,
  clientCode: string,
  year: number
): UseMutationResult<
  ClientBudgetSettings,
  BudgetApiError,
  Partial<UpdateClientBudgetRequest['settings']>
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (settings: Partial<UpdateClientBudgetRequest['settings']>) =>
      updateClientBudget(clientCode, year, settings),
    onSuccess: () => {
      // Invalidate specific client budget (AC2)
      queryClient.invalidateQueries(
        invalidateBudgetQueries.clientBudget(tenantDomain, clientCode, year)
      )
      // Also invalidate client budgets list as it may include summary data
      queryClient.invalidateQueries(
        invalidateBudgetQueries.clientBudgetsList(tenantDomain, year)
      )
      // Invalidate projections summary as it aggregates client budgets
      queryClient.invalidateQueries(
        invalidateBudgetQueries.projectionsSummary(tenantDomain, year)
      )
    },
  })
}

/**
 * Fetch all client budgets for a specific year
 *
 * Automatically caches data for 5 minutes (AC1).
 *
 * @param tenantDomain - Tenant domain
 * @param year - Budget year
 * @param enabled - Whether query should run (default: true)
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useClientBudgetsList('monumenta.localhost', 2025)
 *
 * if (isLoading) return <LoadingSpinner />
 * return <ClientBudgetsTable clients={data.clients} />
 * ```
 */
export function useClientBudgetsList(
  tenantDomain: string,
  year: number,
  enabled = true
): UseQueryResult<ClientBudgetsList, BudgetApiError> {
  return useQuery({
    queryKey: budgetQueryKeys.clientBudgets.list(tenantDomain, year),
    queryFn: () => listClientBudgets(year),
    enabled: enabled && !!tenantDomain && !!year,
    // Inherits staleTime: 5 minutes from global config (AC1)
  })
}

/**
 * Fetch training budget for a specific year
 *
 * Automatically caches data for 5 minutes (AC1).
 *
 * @param tenantDomain - Tenant domain
 * @param year - Budget year
 * @param enabled - Whether query should run (default: true)
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useTrainingBudget('monumenta.localhost', 2025)
 *
 * if (isLoading) return <Skeleton />
 * return <TrainingBudgetCard budget={data} />
 * ```
 */
export function useTrainingBudget(
  tenantDomain: string,
  year: number,
  enabled = true
): UseQueryResult<TrainingBudget, BudgetApiError> {
  return useQuery({
    queryKey: budgetQueryKeys.trainingBudget.detail(tenantDomain, year),
    queryFn: () => getTrainingBudget(year),
    enabled: enabled && !!tenantDomain && !!year,
    // Inherits staleTime: 5 minutes from global config (AC1)
  })
}

/**
 * Update training budget for a specific year
 *
 * Automatically invalidates related queries after successful update (AC2).
 *
 * @param tenantDomain - Tenant domain
 * @param year - Budget year
 *
 * @example
 * ```typescript
 * const updateMutation = useUpdateTrainingBudget('monumenta.localhost', 2025)
 *
 * const handleSave = async (budget: { budget?: number; actualCost?: number }) => {
 *   await updateMutation.mutateAsync(budget)
 * }
 * ```
 */
export function useUpdateTrainingBudget(
  tenantDomain: string,
  year: number
): UseMutationResult<TrainingBudget, BudgetApiError, UpdateTrainingBudgetRequest> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (budget: UpdateTrainingBudgetRequest) =>
      updateTrainingBudget(year, budget),
    onSuccess: () => {
      // Invalidate training budget query (AC2)
      queryClient.invalidateQueries(
        invalidateBudgetQueries.trainingBudget(tenantDomain, year)
      )
    },
  })
}

/**
 * Fetch company-wide projections summary for a specific year
 *
 * Automatically caches data for 5 minutes (AC1).
 * This aggregates all client budgets.
 *
 * @param tenantDomain - Tenant domain
 * @param year - Budget year
 * @param enabled - Whether query should run (default: true)
 *
 * @example
 * ```typescript
 * const { data, isLoading } = useProjectionsSummary('monumenta.localhost', 2025)
 *
 * if (isLoading) return <LoadingSpinner />
 * return <ProjectionsOverview projections={data.projections} />
 * ```
 */
export function useProjectionsSummary(
  tenantDomain: string,
  year: number,
  enabled = true
): UseQueryResult<ProjectionsSummary, BudgetApiError> {
  return useQuery({
    queryKey: budgetQueryKeys.projections.summary(tenantDomain, year),
    queryFn: () => getProjectionsSummary(year),
    enabled: enabled && !!tenantDomain && !!year,
    // Inherits staleTime: 5 minutes from global config (AC1)
  })
}
